import { AnimatePresence, motion } from "motion/react";
import { Children, createContext, isValidElement, useContext } from "react";

import { cn } from "../../lib/cn";
import {
  type StageDataSceneProps,
  type SceneElement,
  type StageProps,
  type StageSceneProps,
} from "./stage-types";

const initialMotionOptions = {
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -10 },
  transition: { duration: 0.2 },
};

const StageContext = createContext<{
  className?: string;
}>({});

function isSceneElement(child: unknown): child is SceneElement {
  return isValidElement(child) && child.type === Scene;
}

function isDataScene<T>(
  props: StageSceneProps<T>,
): props is StageDataSceneProps<T> {
  return typeof props.children === "function";
}

function isActiveScene(props: StageSceneProps<unknown>) {
  if (isDataScene(props)) {
    return props.data != null;
  }

  return props.status === true;
}

function StageBase({ children, className }: StageProps) {
  const childrenArray = Children.toArray(children);

  let sceneToRender = childrenArray.find(
    (child) => isSceneElement(child) && isActiveScene(child.props),
  );

  if (!sceneToRender) {
    sceneToRender = childrenArray.find(
      (child) => isSceneElement(child) && child.props.default,
    );
  }

  return (
    <StageContext.Provider value={{ className }}>
      <AnimatePresence mode="wait">{sceneToRender ?? null}</AnimatePresence>
    </StageContext.Provider>
  );
}

function Scene<T>(props: StageSceneProps<T>) {
  const { className } = useContext(StageContext);

  let sceneChildren;

  if (isDataScene(props)) {
    if (props.data == null) {
      return props.fallback ?? null;
    }

    sceneChildren = props.children(props.data);
  } else if (props.status !== true && props.default !== true) {
    return props.fallback ?? null;
  } else {
    if (props.children == null) return null;

    sceneChildren = props.children;
  }

  const { ref } = props;
  const options = props.options ?? initialMotionOptions;

  return (
    <motion.div
      animate={options.animate}
      className={cn(className, props.className)}
      exit={options.exit}
      initial={options.initial}
      ref={ref}
      transition={options.transition}
    >
      {sceneChildren}
    </motion.div>
  );
}

const Stage = Object.assign(StageBase, { Scene });

export { Scene, Stage };
